import { motion } from "framer-motion";
import { ContentBlock } from '@/data/types';

interface ContentSectionProps {
  title: string;
  content: ContentBlock[];
  colorScheme: "purple" | "pink" | "blue" | "cyan";
  delay?: number;
}

export default function ContentSection({ 
  title, 
  content, 
  colorScheme,
  delay = 0 
}: ContentSectionProps) {
  // Farbschemata für verschiedene Abschnitte
  const colorSchemes = {
    purple: {
      heading: "text-[#b388ff]",
      bgGradient: "bg-gradient-to-tr from-[rgba(120,70,160,0.2)] to-[rgba(120,70,160,0.1)]",
      border: "border-[rgba(179,136,255,0.15)]",
      shadow: "shadow-[0_0_25px_rgba(120,70,160,0.15)]"
    },
    pink: {
      heading: "text-[#ff88ff]",
      bgGradient: "bg-gradient-to-tr from-[rgba(160,70,160,0.2)] to-[rgba(160,70,160,0.1)]",
      border: "border-[rgba(255,136,255,0.15)]",
      shadow: "shadow-[0_0_25px_rgba(160,70,160,0.15)]"
    },
    blue: {
      heading: "text-[#88b8ff]",
      bgGradient: "bg-gradient-to-tr from-[rgba(70,120,160,0.2)] to-[rgba(70,120,160,0.1)]",
      border: "border-[rgba(136,184,255,0.15)]",
      shadow: "shadow-[0_0_25px_rgba(70,120,160,0.15)]"
    },
    cyan: {
      heading: "text-[#88ddff]",
      bgGradient: "bg-gradient-to-tr from-[rgba(70,150,180,0.2)] to-[rgba(70,150,180,0.1)]",
      border: "border-[rgba(136,221,255,0.15)]",
      shadow: "shadow-[0_0_25px_rgba(70,150,180,0.15)]"
    }
  };

  const scheme = colorSchemes[colorScheme];

  const renderContent = (content: ContentBlock[]) => {
    return content.map((block, index) => {
      if (block.listing) {
        return (
          <ul key={index} className="list-disc pl-6 space-y-2 mb-6">
            {block.listing.map((item, itemIndex) => (
              <li key={itemIndex} className="text-white/80">{item}</li>
            ))}
          </ul>
        );
      }

      if (block.subheadline) {
        return (
          <div key={index} className="mb-16 sm:mb-8 last-of-type:mb-4">
            <h3 className="text-xl font-semibold text-white mb-2">{block.subheadline}</h3>
            {block.text && <p className="text-white/80">{block.text}</p>}
          </div>
        );
      }

      return (
        <p key={index} className="text-white/80 mb-8 last-of-type:mb-4">
          {block.text}
        </p>
      );
    });
  };

  return (
    <motion.div
      className="mb-20"
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.8, delay }}
      data-cy="content-section"
    >
      <motion.h2
        className={`text-3xl md:text-4xl font-bold mb-6 ${scheme.heading}`}
        initial={{ opacity: 0, x: -20 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: delay + 0.2 }}
      >
        {title}
      </motion.h2>

      <motion.div
        className={`text-lg leading-relaxed 
                   ${scheme.bgGradient} backdrop-blur-sm 
                   p-4 sm:p-8 rounded-xl border ${scheme.border} ${scheme.shadow}`}
        initial={{ opacity: 0, scale: 0.98 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: delay + 0.3 }}
      >
        <div className="space-y-4">
          {renderContent(content)}
        </div>
      </motion.div>
    </motion.div>
  );
}